import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertTriangle, Search, Flag, Trash2, Plus, Edit3, ChevronDown, ChevronUp, FileText, ExternalLink, RefreshCw, Play, Eye, MessageSquare, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

interface ValidationCommandCenterProps {
  isOpen: boolean;
  onClose: () => void;
  processMapId: number;
  projectId: number;
  validationId: number;
  onValidationUpdate?: () => void;
}

interface ValidationResult {
  process_step_id: string | number;
  process_step_name: string;
  validation_status: string;
  confidence_score: number;
  evidence_quality?: string;
  source_reference?: {
    document_name: string;
    section: string;
    page: string;
    quoted_text: string;
  };
  reasoning?: string;
  assumptions_made?: string[];
  ambiguities_found?: string[];
  missing_information?: string[];
  systems_involved_verification?: {
    explicitly_mentioned: boolean;
    systems_found_in_source: string[];
    systems_not_found_in_source: string[];
    source_reference: string;
    confidence_score: number;
    notes: string;
  };
  stakeholder_verification?: {
    explicitly_named: boolean;
    stakeholder_found_in_source: string | null;
    source_reference: string;
    confidence_score: number;
    notes: string;
  };
  suggested_action?: string;
  user_action?: 'approved' | 'rejected' | 'flagged' | 'edited';
  edited?: boolean;
  user_notes?: string;
  assigned_to?: string;
}

interface GapDetectionResult {
  gap_id: string;
  step_name: string;
  description: string;
  source_reference: {
    document_name: string;
    section: string;
    page: string;
    quoted_text: string;
  };
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  reasoning: string;
  suggested_placement: string;
  confidence_score: number;
  assignee?: string;
  user_action?: 'added' | 'rejected' | 'flagged';
}

interface ProcessStep {
  id: string;
  name: string;
  step_number?: string;
  step_index?: number;
}

interface GapConfiguration {
  gap_id: string;
  insert_after_step: string;
  assignee: string;
  isConfiguring: boolean;
}

const ValidationCommandCenter: React.FC<ValidationCommandCenterProps> = ({
  isOpen,
  onClose,
  processMapId,
  projectId,
  validationId,
  onValidationUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [gapResults, setGapResults] = useState<GapDetectionResult[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'needs_action' | 'clear' | 'flagged'>('all');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [matchRate, setMatchRate] = useState(0);
  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  const [editingStep, setEditingStep] = useState<ValidationResult | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', assignee: '' });
  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [viewingDetails, setViewingDetails] = useState<ValidationResult | null>(null);
  const [revalidating, setRevalidating] = useState(false);
  const [revalidationProgress, setRevalidationProgress] = useState<{
    completed_steps?: number;
    total_steps?: number;
    estimated_progress?: number;
  } | null>(null);
  const [processSteps, setProcessSteps] = useState<ProcessStep[]>([]);
  const [gapConfigurations, setGapConfigurations] = useState<Map<string, GapConfiguration>>(new Map());
  const [currentValidationId, setCurrentValidationId] = useState<number>(validationId);
  const [notesStep, setNotesStep] = useState<ValidationResult | null>(null);
  const [notesForm, setNotesForm] = useState({ notes: '', assigned_to: '' });
  const [stakeholders, setStakeholders] = useState<string[]>([]);
  const [deleteStepConfirmation, setDeleteStepConfirmation] = useState<{
    isOpen: boolean;
    step: ValidationResult | null;
  }>({ isOpen: false, step: null });
  const [customAssignee, setCustomAssignee] = useState<string | null>(null); // ✅ FIX: Use null to indicate "not in custom mode"
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // ✅ NEW: AI Suggestion states
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState<any>(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [suggestionStep, setSuggestionStep] = useState<ValidationResult | null>(null);

  // Update currentValidationId when prop changes
  useEffect(() => {
    setCurrentValidationId(validationId);
  }, [validationId]);

  useEffect(() => {
    console.log('ValidationCommandCenter useEffect:', { isOpen, validationId, processMapId });
    if (isOpen) {
      setIsClosing(false);
      setTimeout(() => setIsAnimating(true), 10);
      if (validationId) {
        fetchValidationData(); // This will also extract stakeholders from the map data
      }
    } else {
      setIsAnimating(false);
    }
  }, [isOpen, validationId, processMapId]);

  const fetchProcessSteps = async () => {
    try {
      console.log('Fetching process steps for processMapId:', processMapId);
      const response = await api.get(`/api/analysis/process-maps/${processMapId}`);
      console.log('Process map API response:', response.data);

      // Try different possible response structures
      const mapData = response.data?.map_data || response.data?.processMap?.map_data || response.data;
      const processes = mapData?.elements?.processes || mapData?.processes || [];

      if (processes.length > 0) {
        const steps = processes.map((step: any) => ({
          id: step.id,
          name: step.name || step.step_name,
          step_number: step.step_number,
          step_index: step.step_index
        }));
        console.log('Process steps loaded:', steps.length, 'steps');
        setProcessSteps(steps);
      } else {
        console.warn('No processes found in response:', response.data);
      }
    } catch (error) {
      console.error('Failed to fetch process steps:', error);
    }
  };

  const extractStakeholdersFromMapData = (mapData: any) => {
    try {
      // Extract stakeholders from map data (same logic as SwimlanesVisualization)
      const processes = mapData?.elements?.processes || mapData?.processes || [];
      const swimlanes = mapData?.elements?.swimlanes || mapData?.swimlanes || [];

      const stakeholderSet = new Set<string>();

      // Extract from processes (actors and assignees)
      processes.forEach((process: any) => {
        if (process.actor && process.actor !== 'Unassigned' && process.actor !== 'Process Owner') {
          if (process.actor.length > 2 && !process.actor.includes('Generated')) {
            stakeholderSet.add(process.actor);
          }
        }
        if (process.assignee && process.assignee !== 'Unassigned' && process.assignee !== 'Process Owner' && !process.actor) {
          if (process.assignee.length > 2 && !process.assignee.includes('Generated')) {
            stakeholderSet.add(process.assignee);
          }
        }
      });

      // Extract from swimlanes (preferred source)
      if (swimlanes && swimlanes.length > 0) {
        swimlanes.forEach((lane: any) => {
          if (lane.name && lane.name !== 'Unassigned' && lane.name !== 'Process Owner') {
            stakeholderSet.add(lane.name);
          }
        });
      }

      const stakeholderList = Array.from(stakeholderSet).filter(s => s && s.trim());
      console.log('Stakeholders extracted from map data:', stakeholderList.length, 'stakeholders', stakeholderList);
      setStakeholders(stakeholderList);
    } catch (error) {
      console.error('Failed to extract stakeholders from map data:', error);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Start re-validation
  const startRevalidation = async () => {
    setRevalidating(true);
    setRevalidationProgress(null);

    try {
      await api.post(`/api/validation/process-map/${processMapId}/validate`, {
        project_id: projectId
      });
      toast.success('Re-validation started');

      // Start polling for progress
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }

      pollingRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(`/api/validation/process-map/${processMapId}/validation-status`);
          setRevalidationProgress({
            completed_steps: statusRes.data?.completed_steps,
            total_steps: statusRes.data?.total_steps,
            estimated_progress: statusRes.data?.estimated_progress
          });

          if (statusRes.data?.status === 'COMPLETED') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setRevalidating(false);
            toast.success('Re-validation completed');
            // Get the new validation ID from the response
            const newValidationId = statusRes.data?.validation_id;
            if (newValidationId) {
              setCurrentValidationId(newValidationId);
            }
            // Small delay to ensure backend has finished processing
            setTimeout(() => {
              fetchValidationData(newValidationId);
              onValidationUpdate?.();
            }, 500);
          } else if (statusRes.data?.status === 'FAILED') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setRevalidating(false);
            toast.error('Re-validation failed');
          }
        } catch (err) {
          // Continue polling
        }
      }, 3000);

      // Timeout after 10 minutes
      setTimeout(() => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setRevalidating(false);
      }, 600000);

    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.success('Validation already in progress');
        // Start polling for existing validation
        pollingRef.current = setInterval(async () => {
          try {
            const statusRes = await api.get(`/api/validation/process-map/${processMapId}/validation-status`);
            setRevalidationProgress({
              completed_steps: statusRes.data?.completed_steps,
              total_steps: statusRes.data?.total_steps,
              estimated_progress: statusRes.data?.estimated_progress
            });

            if (statusRes.data?.status === 'COMPLETED') {
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
              setRevalidating(false);
              toast.success('Validation completed');
              // Get the new validation ID from the response
              const newValidationId = statusRes.data?.validation_id;
              if (newValidationId) {
                setCurrentValidationId(newValidationId);
              }
              // Small delay to ensure backend has finished processing
              setTimeout(() => {
                fetchValidationData(newValidationId);
                onValidationUpdate?.();
              }, 500);
            } else if (statusRes.data?.status === 'FAILED') {
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
              setRevalidating(false);
              toast.error('Validation failed');
            }
          } catch (err) {
            // Continue polling
          }
        }, 3000);
      } else {
        console.error('Failed to start re-validation:', error);
        toast.error('Failed to start re-validation');
        setRevalidating(false);
      }
    }
  };

  const fetchValidationData = async (overrideValidationId?: number) => {
    setLoading(true);
    try {
      const idToUse = overrideValidationId || currentValidationId || validationId;
      const response = await api.get(`/api/validation/validation/${idToUse}/report`);
      const data = response.data;

      // Extract process steps first so we can fix step numbering
      let currentStepNameMap = new Map<string, string>();
      const mapData = data.process_map?.map_data;
      if (mapData?.elements?.processes) {
        const steps: ProcessStep[] = mapData.elements.processes.map((step: any) => ({
          id: step.id,
          name: step.name || step.step_name,
          step_number: step.step_number,
          step_index: step.step_index
        }));
        console.log('Process steps loaded from validation report:', steps.length, 'steps');
        setProcessSteps(steps);
        extractStakeholdersFromMapData(mapData);

        // Build ID → current name map so we can fix stale step numbers in validation results
        steps.forEach(s => {
          if (s.id) currentStepNameMap.set(String(s.id), s.name);
        });
      }

      // Update each validation result's displayed name to match current process map step number
      const rawResults: ValidationResult[] = data.validation_results || [];
      const syncedResults = rawResults.map(r => {
        const currentName = currentStepNameMap.get(String(r.process_step_id));
        if (currentName && currentName !== r.process_step_name) {
          return { ...r, process_step_name: currentName };
        }
        return r;
      });

      setValidationResults(syncedResults);
      setGapResults(data.gap_detection_results || []);
      calculateScores(syncedResults, data.gap_detection_results || []);
    } catch (error) {
      console.error('Failed to fetch validation data:', error);
      toast.error('Failed to load validation data');
    } finally {
      setLoading(false);
    }
  };

  const calculateScores = (results: ValidationResult[], gaps: GapDetectionResult[]) => {
    // Match backend calculation logic exactly
    const approved = results.filter(r =>
      r.user_action === 'approved' ||
      (r.validation_status === 'CLEAR_MATCH' && !r.user_action)
    ).length;

    const rejected = results.filter(r => r.user_action === 'rejected').length;
    const total = results.length - rejected; // Exclude rejected steps from total

    const needsAction = results.filter(r =>
      r.validation_status !== 'CLEAR_MATCH' &&
      !r.user_action
    ).length + gaps.filter(g => !g.user_action).length;

    setMatchRate(total > 0 ? Math.round((approved / total) * 100) : 0);
    setNeedsReviewCount(needsAction);
  };

  const getFilteredResults = () => {
    let filtered = [...validationResults];

    switch (activeFilter) {
      case 'needs_action':
        filtered = filtered.filter(r =>
          r.validation_status !== 'CLEAR_MATCH' && !r.user_action
        );
        break;
      case 'clear':
        filtered = filtered.filter(r =>
          r.validation_status === 'CLEAR_MATCH' || r.user_action === 'approved'
        );
        break;
      case 'flagged':
        filtered = filtered.filter(r => r.user_action === 'flagged');
        break;
    }

    // Sort by step number so list always appears in process order
    filtered.sort((a, b) => {
      const numA = parseInt(String(a.process_step_name).match(/^Step\s*(\d+)/i)?.[1] ?? '9999');
      const numB = parseInt(String(b.process_step_name).match(/^Step\s*(\d+)/i)?.[1] ?? '9999');
      return numA - numB;
    });

    return filtered;
  };

  const handleBulkApprove = async () => {
    const clearMatches = validationResults.filter(r =>
      r.validation_status === 'CLEAR_MATCH' && !r.user_action
    );

    if (clearMatches.length === 0) {
      toast.error('No clear matches to approve');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/api/validation/validation/${currentValidationId}/bulk-approve`, {
        step_ids: clearMatches.map(r => r.process_step_id)
      });

      const updated = validationResults.map(r => {
        if (r.validation_status === 'CLEAR_MATCH' && !r.user_action) {
          return { ...r, user_action: 'approved' as const };
        }
        return r;
      });

      setValidationResults(updated);
      calculateScores(updated, gapResults);
      toast.success(`Approved ${clearMatches.length} clear matches`);
      onValidationUpdate?.();
    } catch (error) {
      console.error('Bulk approve failed:', error);
      toast.error('Failed to bulk approve');
    } finally {
      setSaving(false);
    }
  };

  const handleStepAction = async (stepId: string | number, action: 'approved' | 'rejected' | 'flagged') => {
    setSaving(true);
    try {
      const response = await api.post(`/api/validation/validation/${currentValidationId}/step-action`, {
        step_id: stepId,
        action
      });

      let updated: ValidationResult[];

      if (action === 'rejected') {
        // Remove rejected steps from the list entirely
        updated = validationResults.filter(r => r.process_step_id !== stepId);
      } else {
        // For other actions, update the status
        updated = validationResults.map(r => {
          if (r.process_step_id === stepId) {
            return { ...r, user_action: action };
          }
          return r;
        });
      }

      setValidationResults(updated);
      calculateScores(updated, gapResults);

      // Update match percentage from backend response
      if (response.data.new_match_percentage !== undefined) {
        setMatchRate(response.data.new_match_percentage);
      }

      // Show appropriate toast message
      if (action === 'rejected' && response.data.step_removed_from_map) {
        toast.success('Step rejected and removed from process map');
      } else {
        toast.success(`Step ${action}`);
      }

      onValidationUpdate?.();
    } catch (error) {
      console.error('Step action failed:', error);
      toast.error(`Failed to ${action} step`);
    } finally {
      setSaving(false);
    }
  };

  const handleClearAction = async (stepId: string | number) => {
    setSaving(true);
    try {
      const response = await api.post(`/api/validation/validation/${currentValidationId}/step-action-clear`, {
        step_id: stepId
      });

      const updated = validationResults.map(r => {
        if (r.process_step_id === stepId) {
          // Remove user_action and edited flags to revert to original state
          const { user_action, edited, ...rest } = r;
          return rest;
        }
        return r;
      });

      setValidationResults(updated);
      calculateScores(updated, gapResults);

      // Update match percentage from backend response
      if (response.data.new_match_percentage !== undefined) {
        setMatchRate(response.data.new_match_percentage);
      }

      toast.success('Action undone successfully');
      onValidationUpdate?.();
    } catch (error) {
      console.error('Clear action failed:', error);
      toast.error('Failed to undo action');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenNotes = (result: ValidationResult) => {
    setNotesStep(result);
    setNotesForm({
      notes: result.user_notes || '',
      assigned_to: result.assigned_to || ''
    });
  };

  const handleSaveNotes = async () => {
    if (!notesStep) return;

    setSaving(true);
    try {
      await api.post(`/api/validation/validation/${currentValidationId}/step-notes`, {
        step_id: notesStep.process_step_id,
        notes: notesForm.notes,
        assigned_to: notesForm.assigned_to
      });

      const updated = validationResults.map(r => {
        if (r.process_step_id === notesStep.process_step_id) {
          return {
            ...r,
            user_notes: notesForm.notes,
            assigned_to: notesForm.assigned_to
          };
        }
        return r;
      });

      setValidationResults(updated);
      setNotesStep(null);
      toast.success('Notes saved');
      onValidationUpdate?.();
    } catch (error) {
      console.error('Save notes failed:', error);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (stepId: string | number) => {
    setSaving(true);
    try {
      const response = await api.delete(`/api/process-maps/${processMapId}/steps/${stepId}`, {
        data: { project_id: projectId }
      });

      if (response.data.success) {
        const updated = validationResults.filter(r => r.process_step_id !== stepId);
        setValidationResults(updated);
        calculateScores(updated, gapResults);
        toast.success('Step deleted from process map');
        onValidationUpdate?.();
      }
    } catch (error: any) {
      console.error('Delete step failed:', error);
      toast.error(error.response?.data?.message || 'Failed to delete step');
    } finally {
      setSaving(false);
      setDeleteStepConfirmation({ isOpen: false, step: null });
    }
  };

  const handleGapAction = async (gapId: string, action: 'added' | 'rejected' | 'flagged') => {
    setSaving(true);
    try {
      const config = gapConfigurations.get(gapId);
      const requestBody: any = {
        gap_id: gapId,
        action,
        process_map_id: processMapId
      };

      // Add configuration parameters if adding to map
      if (action === 'added' && config) {
        if (config.insert_after_step) {
          requestBody.insert_after_step = config.insert_after_step;
        }
        if (config.assignee) {
          requestBody.assignee = config.assignee;
        }
      }

      console.log('Gap action request:', requestBody);
      const response = await api.post(`/api/validation/validation/${currentValidationId}/gap-action`, requestBody);
      console.log('Gap action response:', response.data);

      const updated = gapResults.map(g => {
        if (g.gap_id === gapId) {
          return { ...g, user_action: action };
        }
        return g;
      });

      setGapResults(updated);
      calculateScores(validationResults, updated);

      // Clear configuration for this gap
      const newConfigs = new Map(gapConfigurations);
      newConfigs.delete(gapId);
      setGapConfigurations(newConfigs);

      if (action === 'added') {
        const insertionDetails = response.data?.insertion_details;
        if (insertionDetails) {
          toast.success(`Step added as Step ${insertionDetails.new_step_number} after "${insertionDetails.inserted_after}"`);
        } else {
          toast.success('Step added to process map');
        }
        // Refresh validation data to show updated process map
        await fetchValidationData();
      } else {
        toast.success(`Gap ${action}`);
      }
      onValidationUpdate?.();
    } catch (error) {
      console.error('Gap action failed:', error);
      toast.error(`Failed to ${action} gap`);
    } finally {
      setSaving(false);
    }
  };

  const toggleGapConfiguration = (gapId: string) => {
    const newConfigs = new Map(gapConfigurations);
    const existing = newConfigs.get(gapId);

    if (existing?.isConfiguring) {
      newConfigs.delete(gapId);
    } else {
      // Find suggested placement step
      const gap = gapResults.find(g => g.gap_id === gapId);
      let suggestedStepId = '';

      if (processSteps.length > 0) {
        if (gap?.suggested_placement) {
          const lowerPlacement = gap.suggested_placement.toLowerCase();

          // First: extract step number from placement like "After step 14", "Before step 5", "After Step 2"
          const stepNumMatch = lowerPlacement.match(/step\s*(\d+)/);
          if (stepNumMatch) {
            const targetNum = stepNumMatch[1];
            const matchedStep = processSteps.find(step =>
              String(step.step_number) === targetNum || String(step.step_index) === targetNum
            );
            if (matchedStep) suggestedStepId = matchedStep.id;
          }

          // Fallback: try name text matching
          if (!suggestedStepId) {
            const matchedStep = processSteps.find(step =>
              step.name.toLowerCase().includes(lowerPlacement) ||
              lowerPlacement.includes(step.name.toLowerCase())
            );
            if (matchedStep) suggestedStepId = matchedStep.id;
          }
        }

        // Default to last step only if no match found at all
        if (!suggestedStepId) {
          suggestedStepId = processSteps[processSteps.length - 1]?.id || '';
        }
      }

      console.log('Toggle gap configuration:', {
        gapId,
        suggestedStepId,
        processStepsCount: processSteps.length,
        suggestedPlacement: gap?.suggested_placement
      });

      newConfigs.set(gapId, {
        gap_id: gapId,
        insert_after_step: suggestedStepId,
        assignee: gap?.assignee || '',
        isConfiguring: true
      });
    }

    setGapConfigurations(newConfigs);
  };

  const updateGapConfiguration = (gapId: string, field: 'insert_after_step' | 'assignee', value: string) => {
    const newConfigs = new Map(gapConfigurations);
    const existing = newConfigs.get(gapId);

    if (existing) {
      newConfigs.set(gapId, {
        ...existing,
        [field]: value
      });
      setGapConfigurations(newConfigs);
    }
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleEditStep = (result: ValidationResult) => {
    setEditingStep(result);
    const currentAssignee = result.stakeholder_verification?.stakeholder_found_in_source || '';
    setEditForm({
      name: result.process_step_name || '',
      description: result.reasoning || '',
      assignee: currentAssignee
    });
    // ✅ FIX: Reset to null to hide custom input
    setCustomAssignee(null);
  };

  const handleSaveEdit = async () => {
    if (!editingStep) return;

    setSaving(true);
    try {
      const response = await api.post(`/api/validation/validation/${currentValidationId}/edit-step`, {
        step_id: editingStep.process_step_id,
        process_map_id: processMapId,
        updates: {
          name: editForm.name,
          description: editForm.description || undefined,
          assignee: editForm.assignee || undefined
        }
      });

      const updated = validationResults.map(r => {
        if (r.process_step_id === editingStep.process_step_id) {
          return { ...r, user_action: 'approved' as const, process_step_name: editForm.name, edited: true };
        }
        return r;
      });

      setValidationResults(updated);
      calculateScores(updated, gapResults);

      // Update match percentage from backend response
      if (response.data.new_match_percentage !== undefined) {
        setMatchRate(response.data.new_match_percentage);
      }

      setEditingStep(null);

      // ✅ NEW: Add new stakeholder to dropdown immediately
      if (response.data.stakeholder_added && response.data.new_stakeholder) {
        // Add to stakeholders list if not already present
        if (!stakeholders.includes(response.data.new_stakeholder)) {
          setStakeholders([...stakeholders, response.data.new_stakeholder]);
          console.log('✅ Added new stakeholder to dropdown:', response.data.new_stakeholder);
        }

        toast.success(`Step updated! "${response.data.new_stakeholder}" added to Stakeholders & Responsibilities. Please refresh that tab to see it.`, {
          duration: 5000
        });
      } else {
        toast.success('Step updated and approved successfully');
      }

      onValidationUpdate?.();
    } catch (error) {
      console.error('Edit step failed:', error);
      toast.error('Failed to update step');
    } finally {
      setSaving(false);
    }
  };

  // ✅ NEW: AI Suggestion Handler
  const handleGetAISuggestion = async (result: ValidationResult) => {
    setSuggestionStep(result);
    setLoadingSuggestion(true);
    try {
      const response = await api.post(
        `/api/validation/validation/${currentValidationId}/suggest-fix/${result.process_step_id}`,
        {}
      );
      setCurrentSuggestion(response.data);
      setShowSuggestionModal(true);
    } catch (error: any) {
      console.error('Failed to get AI suggestion:', error);
      toast.error(error.response?.data?.error || 'Failed to generate AI suggestion');
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleAcceptSuggestion = () => {
    if (!currentSuggestion || !suggestionStep) return;

    setEditingStep(suggestionStep);
    setEditForm({
      name: currentSuggestion.suggestion.step_name,
      description: currentSuggestion.suggestion.description || '',
      assignee: ''
    });
    setShowSuggestionModal(false);
    setCurrentSuggestion(null);
    setSuggestionStep(null);
  };

  const [quickAddDone, setQuickAddDone] = useState(false);

  const handleQuickAdd = async () => {
    const enhancements = validationResults.filter(r =>
      (r.systems_involved_verification?.systems_not_found_in_source?.length ?? 0) > 0 ||
      (r.missing_information?.length ?? 0) > 0
    );

    if (enhancements.length === 0) {
      toast.error('No enhancements to add');
      return;
    }

    // Show what will be added
    const totalSystems = enhancements.reduce((sum, e) =>
      sum + (e.systems_involved_verification?.systems_not_found_in_source?.length || 0), 0
    );
    const totalMissing = enhancements.reduce((sum, e) =>
      sum + (e.missing_information?.length || 0), 0
    );

    setSaving(true);
    try {
      const response = await api.post(`/api/validation/validation/${currentValidationId}/quick-add`, {
        process_map_id: processMapId,
        enhancements: enhancements.map(e => ({
          step_id: e.process_step_id,
          systems_to_add: e.systems_involved_verification?.systems_not_found_in_source || [],
          missing_info: e.missing_information || []
        }))
      });

      const enhancedCount = response.data?.enhanced_count || enhancements.length;

      // Show detailed success message
      let message = `Enhanced ${enhancedCount} steps`;
      if (totalSystems > 0) message += ` with ${totalSystems} systems`;
      if (totalMissing > 0) message += `${totalSystems > 0 ? ' and' : ' with'} ${totalMissing} notes`;

      toast.success(message);
      setQuickAddDone(true); // Mark as done so button shows completed state
      onValidationUpdate?.();
    } catch (error) {
      console.error('Quick add failed:', error);
      toast.error('Failed to add enhancements');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CLEAR_MATCH': return 'bg-green-100 text-green-800 border-green-200';
      case 'INFERENCE': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'AMBIGUITY': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MISSING': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CLEAR_MATCH': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'INFERENCE': return <Search className="w-4 h-4 text-yellow-600" />;
      case 'AMBIGUITY': return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'MISSING': return <X className="w-4 h-4 text-red-600" />;
      default: return <AlertTriangle className="w-4 h-4 text-gray-600" />;
    }
  };

  const clearMatchCount = validationResults.filter(r => r.validation_status === 'CLEAR_MATCH' && !r.user_action).length;
  const filteredResults = getFilteredResults();

  if (!isOpen && !isClosing) return null;

  return (
    <>
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-[100] ${
          isAnimating ? 'opacity-30' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      <div className={`fixed inset-y-0 right-0 w-1/2 bg-white shadow-2xl z-[101] flex flex-col border-l border-gray-200 transform transition-transform duration-300 ease-out ${
        isAnimating ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Step Validation</h2>
          <button onClick={handleClose} className="p-1 hover:bg-gray-200 rounded">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-green-600">{matchRate}%</div>
            <div className="text-xs text-gray-500">Match Rate</div>
            <div className="text-xs text-gray-400">{validationResults.filter(r => r.user_action === 'approved' || r.validation_status === 'CLEAR_MATCH').length} of {validationResults.length} validated</div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-orange-600">{needsReviewCount}</div>
            <div className="text-xs text-gray-500">Need Review</div>
            <div className="text-xs text-gray-400">Requires action</div>
          </div>
        </div>

        {/* Re-validate button and progress */}
        {revalidating ? (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Re-validating...
              </span>
              <span className="text-sm text-blue-600 font-semibold">
                {revalidationProgress?.completed_steps !== undefined
                  ? `${revalidationProgress.completed_steps}/${revalidationProgress.total_steps} steps`
                  : 'Starting...'}
              </span>
            </div>
            {revalidationProgress?.estimated_progress !== undefined && (
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${revalidationProgress.estimated_progress}%` }}
                />
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={startRevalidation}
            className="w-full mb-4 flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Re-validate
          </button>
        )}

        <div className="flex gap-2 mb-4">
          <button
            onClick={handleBulkApprove}
            disabled={saving || clearMatchCount === 0 || revalidating}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <CheckCircle className="w-4 h-4" />
            Approve All Clear ({clearMatchCount})
          </button>
          <button
            onClick={handleQuickAdd}
            disabled={saving || quickAddDone || validationResults.filter(r => (r.systems_involved_verification?.systems_not_found_in_source?.length ?? 0) > 0 || (r.missing_information?.length ?? 0) > 0).length === 0}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
              quickAddDone
                ? 'bg-green-500 text-white cursor-not-allowed'
                : 'bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
            title="Add systems and notes from validation to process map steps"
          >
            {quickAddDone ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Added
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Quick Add ({validationResults.filter(r => (r.systems_involved_verification?.systems_not_found_in_source?.length ?? 0) > 0 || (r.missing_information?.length ?? 0) > 0).length})
              </>
            )}
          </button>
        </div>

        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {[
            { key: 'all', label: `All (${validationResults.length})` },
            { key: 'needs_action', label: `Needs Action (${needsReviewCount})` },
            { key: 'clear', label: `Clear (${validationResults.filter(r => r.validation_status === 'CLEAR_MATCH').length})` },
            { key: 'flagged', label: `Flagged (${validationResults.filter(r => r.user_action === 'flagged').length})` }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key as any)}
              className={`flex-1 px-2 py-1 text-xs rounded ${
                activeFilter === filter.key
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <>
            {(() => {
              const clearMatches = filteredResults.filter(r => r.validation_status === 'CLEAR_MATCH');
              const inferences = filteredResults.filter(r => r.validation_status === 'INFERENCE');
              const ambiguities = filteredResults.filter(r => r.validation_status === 'AMBIGUITY');
              const missing = filteredResults.filter(r => r.validation_status === 'MISSING');

              const renderStepCard = (result: ValidationResult, borderColor: string, index: number) => {
                const itemId = `step-${result.process_step_id}`;
                // Override border color to green if step is approved
                const finalBorderColor = result.user_action === 'approved' ? 'border-green-500' : borderColor;

                // ✅ FIX: Get color for numbered badge based on validation status
                const getBadgeColor = () => {
                  if (result.user_action === 'approved') return 'bg-green-100 text-green-700';
                  if (result.validation_status === 'CLEAR_MATCH') return 'bg-blue-100 text-blue-700';
                  if (result.validation_status === 'INFERENCE') return 'bg-yellow-100 text-yellow-700';
                  if (result.validation_status === 'AMBIGUITY') return 'bg-orange-100 text-orange-700';
                  if (result.validation_status === 'MISSING') return 'bg-red-100 text-red-700';
                  return 'bg-gray-100 text-gray-700';
                };

                return (
                  <div key={itemId} className={`bg-white border-l-4 ${finalBorderColor} rounded-lg shadow-sm p-4 mb-3`}>
                    <div className="flex items-start gap-3">
                      <span className={`text-xs font-bold ${getBadgeColor()} w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0`}>
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900 leading-relaxed mb-2">
                          {result.process_step_name}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {result.source_reference?.page || 'N/A'}
                          </span>
                          <span className={`flex items-center gap-1 ${
                            (result.confidence_score || 0) >= 90 ? 'text-green-600' :
                            (result.confidence_score || 0) >= 70 ? 'text-yellow-600' : 'text-orange-600'
                          }`}>
                            {result.validation_status === 'AMBIGUITY' && <AlertTriangle className="w-3 h-3" />}
                            {result.confidence_score}% confidence
                          </span>
                          {result.user_action && (
                            <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                              {result.user_action}
                            </span>
                          )}
                        </div>

                        {result.reasoning && (
                          <div className={`text-xs p-3 rounded mb-3 ${
                            result.user_action === 'approved' ? 'bg-green-50 border border-green-200' :
                            result.validation_status === 'INFERENCE' ? 'bg-yellow-50 border border-yellow-200' :
                            result.validation_status === 'AMBIGUITY' ? 'bg-orange-50 border border-orange-200' :
                            result.validation_status === 'MISSING' ? 'bg-red-50 border border-red-200' :
                            'bg-gray-50 border border-gray-200'
                          }`}>
                            <div className="flex items-start gap-2">
                              {result.user_action === 'approved' ? <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" /> :
                              result.validation_status === 'INFERENCE' ? <Search className="w-3 h-3 text-yellow-600 mt-0.5 flex-shrink-0" /> :
                              result.validation_status === 'AMBIGUITY' ? <AlertTriangle className="w-3 h-3 text-orange-600 mt-0.5 flex-shrink-0" /> :
                              result.validation_status === 'MISSING' ? <X className="w-3 h-3 text-red-600 mt-0.5 flex-shrink-0" /> : null}
                              <span className="text-gray-700 leading-relaxed">{result.reasoning}</span>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => setViewingDetails(result)}
                            className="flex items-center justify-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200"
                          >
                            <Eye className="w-3 h-3" />
                            View Details
                          </button>
                          <button
                            onClick={() => handleOpenNotes(result)}
                            className={`flex items-center justify-center gap-1 px-4 py-2 rounded text-xs font-medium ${
                              result.user_notes || result.assigned_to
                                ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <MessageSquare className="w-3 h-3" />
                            Notes
                          </button>
                          {!result.user_action ? (
                            <>
                              {result.validation_status === 'CLEAR_MATCH' ? (
                                <button
                                  onClick={() => handleStepAction(result.process_step_id, 'approved')}
                                  disabled={saving}
                                  className="flex items-center justify-center gap-1 px-4 py-2 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                  Confirm
                                </button>
                              ) : result.validation_status === 'AMBIGUITY' ? (
                                <>
                                  <button
                                    onClick={() => handleGetAISuggestion(result)}
                                    disabled={saving || loadingSuggestion}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                                    title="Get AI suggestion to improve this step"
                                  >
                                    <Sparkles className="w-3 h-3" />
                                    {loadingSuggestion ? 'Loading...' : 'AI Suggestion'}
                                  </button>
                                  <button
                                    onClick={() => handleEditStep(result)}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-white border border-yellow-400 text-yellow-700 rounded text-xs font-medium hover:bg-yellow-50 disabled:opacity-50"
                                  >
                                    <Edit3 className="w-3 h-3" />
                                    Clarify & Edit
                                  </button>
                                  <button
                                    onClick={() => handleStepAction(result.process_step_id, 'flagged')}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-white border border-red-300 text-red-700 rounded text-xs font-medium hover:bg-red-50 disabled:opacity-50"
                                  >
                                    <Flag className="w-3 h-3" />
                                    Flag for Review
                                  </button>
                                </>
                              ) : (
                                <>
                                  {(result.validation_status === 'INFERENCE' || result.validation_status === 'MISSING') && (
                                    <button
                                      onClick={() => handleGetAISuggestion(result)}
                                      disabled={saving || loadingSuggestion}
                                      className="flex items-center justify-center gap-1 px-4 py-2 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                                      title="Get AI suggestion to improve this step"
                                    >
                                      <Sparkles className="w-3 h-3" />
                                      {loadingSuggestion ? 'Loading...' : 'AI Suggestion'}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleStepAction(result.process_step_id, 'approved')}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                                  >
                                    <CheckCircle className="w-3 h-3" />
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => handleEditStep(result)}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-white border border-orange-300 text-orange-700 rounded text-xs font-medium hover:bg-orange-50 disabled:opacity-50"
                                  >
                                    <Edit3 className="w-3 h-3" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleStepAction(result.process_step_id, 'rejected')}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-white border border-red-300 text-red-700 rounded text-xs font-medium hover:bg-red-50 disabled:opacity-50"
                                  >
                                    <X className="w-3 h-3" />
                                    Reject
                                  </button>
                                  <button
                                    onClick={() => setDeleteStepConfirmation({ isOpen: true, step: result })}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                                    title="Permanently delete this step from the process map"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    Delete
                                  </button>
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditStep(result)}
                                disabled={saving}
                                className="flex items-center justify-center gap-1 px-4 py-2 bg-white border border-blue-400 text-blue-700 rounded text-xs font-medium hover:bg-blue-50 disabled:opacity-50"
                              >
                                <Edit3 className="w-3 h-3" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleClearAction(result.process_step_id)}
                                disabled={saving}
                                className="flex items-center justify-center gap-1 px-4 py-2 bg-gray-600 text-white rounded text-xs font-medium hover:bg-gray-700 disabled:opacity-50"
                              >
                                <X className="w-3 h-3" />
                                Undo
                              </button>
                              <button
                                onClick={() => setDeleteStepConfirmation({ isOpen: true, step: result })}
                                disabled={saving}
                                className="flex items-center justify-center gap-1 px-4 py-2 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                                title="Permanently delete this step from the process map"
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              };

              return (
                <>
                  {clearMatches.length > 0 && (activeFilter === 'all' || activeFilter === 'clear') && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">Clear Matches</span>
                        <span className="ml-auto text-sm text-green-600 font-semibold">{clearMatches.length}</span>
                      </div>
                      {clearMatches.map((result, idx) => renderStepCard(result, 'border-green-500', idx))}
                    </div>
                  )}

                  {inferences.length > 0 && (activeFilter === 'all' || activeFilter === 'needs_action') && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3 p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                        <Search className="w-5 h-5 text-yellow-600" />
                        <span className="font-medium text-yellow-800">Inferences Made</span>
                        <span className="ml-auto text-sm text-yellow-600 font-semibold">{inferences.length}</span>
                      </div>
                      {inferences.map((result, idx) => renderStepCard(result, 'border-yellow-500', idx))}
                    </div>
                  )}

                  {ambiguities.length > 0 && (activeFilter === 'all' || activeFilter === 'needs_action') && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3 p-3 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                        <span className="font-medium text-orange-800">Ambiguities Found</span>
                        <span className="ml-auto text-sm text-orange-600 font-semibold">{ambiguities.length}</span>
                      </div>
                      {ambiguities.map((result, idx) => renderStepCard(result, 'border-orange-500', idx))}
                    </div>
                  )}

                  {missing.length > 0 && (activeFilter === 'all' || activeFilter === 'needs_action') && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3 p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
                        <X className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-800">Missing Steps</span>
                        <span className="ml-auto text-sm text-red-600 font-semibold">{missing.length}</span>
                      </div>
                      {missing.map((result, idx) => renderStepCard(result, 'border-red-500', idx))}
                    </div>
                  )}
                </>
              );
            })()}

            {gapResults.length > 0 && activeFilter === 'all' && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3 p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
                  <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                    <span className="text-white text-xs">!</span>
                  </div>
                  <span className="font-medium text-red-800">Gaps Detected (In Source, Missing from Map)</span>
                  <span className="ml-auto text-sm text-red-600 font-semibold">
                    {gapResults.filter(g => !g.user_action).length} remaining
                    {gapResults.some(g => g.user_action) && (
                      <span className="ml-2 text-green-600 font-normal">
                        ({gapResults.filter(g => g.user_action).length} resolved)
                      </span>
                    )}
                  </span>
                </div>
                {gapResults.map((gap) => {
                  const itemId = `gap-${gap.gap_id}`;
                  const config = gapConfigurations.get(gap.gap_id);
                  const isConfiguring = config?.isConfiguring || false;

                  return (
                    <div key={itemId} className={`bg-white border-2 border-dashed rounded-lg p-4 mb-3 ${
                      gap.user_action === 'added' ? 'border-green-300 bg-green-50' : 'border-red-300'
                    }`}>
                      <div className="flex items-start gap-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded whitespace-nowrap ${
                          gap.user_action === 'added' ? 'text-green-700 bg-green-100' : 'text-red-600 bg-red-100'
                        }`}>
                          {gap.user_action === 'added' ? 'ADDED' : 'NEW'}
                        </span>
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900 leading-relaxed mb-2">
                            {gap.step_name}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3 flex-wrap">
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {gap.source_reference?.page || 'N/A'}
                            </span>
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              gap.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                              gap.severity === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                              gap.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {gap.severity}
                            </span>
                            <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                              {gap.confidence_score}% confidence
                            </span>
                            {gap.user_action && (
                              <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800">
                                {gap.user_action}
                              </span>
                            )}
                          </div>

                          {gap.description && (
                            <div className={`text-xs p-3 rounded mb-3 ${
                              gap.user_action === 'added' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                            }`}>
                              <div className="flex items-start gap-2">
                                <div className={`w-3 h-3 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0 ${
                                  gap.user_action === 'added' ? 'bg-green-500' : 'bg-red-500'
                                }`}>
                                  <span className="text-white text-[8px]">{gap.user_action === 'added' ? '✓' : '!'}</span>
                                </div>
                                <span className="text-gray-700 leading-relaxed">{gap.description}</span>
                              </div>
                            </div>
                          )}

                          {gap.suggested_placement && (
                            <div className="text-xs p-3 rounded mb-3 bg-purple-50 border border-purple-200">
                              <div className="flex items-start gap-2">
                                <Plus className="w-3 h-3 text-purple-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-medium text-purple-700">Suggested Placement: </span>
                                  <span className="text-gray-700">{gap.suggested_placement}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {gap.reasoning && (
                            <div className="text-xs p-3 rounded mb-3 bg-gray-50 border border-gray-200">
                              <div className="flex items-start gap-2">
                                <Search className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700 leading-relaxed">{gap.reasoning}</span>
                              </div>
                            </div>
                          )}

                          {gap.source_reference?.quoted_text && (
                            <div className="text-xs p-3 rounded mb-3 bg-blue-50 border border-blue-200">
                              <div className="text-xs text-blue-600 font-medium mb-1">Source Quote:</div>
                              <div className="text-gray-700 italic">"{gap.source_reference.quoted_text}"</div>
                              {gap.source_reference.document_name && (
                                <div className="text-xs text-blue-500 mt-1">
                                  - {gap.source_reference.document_name}
                                  {gap.source_reference.section && `, ${gap.source_reference.section}`}
                                </div>
                              )}
                            </div>
                          )}

                          {!gap.user_action && (
                            <>
                              {isConfiguring && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 space-y-3">
                                  <div className="text-xs font-semibold text-gray-700 uppercase">Configure Step Insertion</div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Insert After Step</label>
                                    <select
                                      value={config?.insert_after_step || ''}
                                      onChange={(e) => updateGapConfiguration(gap.gap_id, 'insert_after_step', e.target.value)}
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    >
                                      <option value="">-- At the beginning --</option>
                                      {processSteps.map((step) => {
                                        const cleanName = step.name
                                          .replace(/^Step\s+\d+\s*[-:]\s*/i, '')
                                          .replace(/:\s*.+$/, '')
                                          .trim();
                                        const label = `Step ${step.step_number ?? step.step_index}: ${cleanName}`;
                                        return (
                                          <option key={step.id} value={step.id}>
                                            {label.substring(0, 70)}{label.length > 70 ? '...' : ''}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Assignee / Stakeholder</label>
                                    <select
                                      value={config?.assignee === '__custom__' || (config?.assignee && !stakeholders.includes(config.assignee)) ? '__custom__' : (config?.assignee || '')}
                                      onChange={(e) => {
                                        if (e.target.value === '__custom__') {
                                          updateGapConfiguration(gap.gap_id, 'assignee', '__custom__');
                                        } else {
                                          updateGapConfiguration(gap.gap_id, 'assignee', e.target.value);
                                        }
                                      }}
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                                    >
                                      <option value="">Select stakeholder...</option>
                                      {stakeholders.map((stakeholder, index) => (
                                        <option key={index} value={stakeholder}>
                                          {stakeholder}
                                        </option>
                                      ))}
                                      <option value="__custom__">-- Enter custom value --</option>
                                    </select>
                                    {(config?.assignee === '__custom__' || (config?.assignee && !stakeholders.includes(config.assignee))) && (
                                      <input
                                        type="text"
                                        value={config?.assignee === '__custom__' ? '' : (config?.assignee || '')}
                                        onChange={(e) => updateGapConfiguration(gap.gap_id, 'assignee', e.target.value)}
                                        placeholder="e.g., Operations Team, Risk Manager"
                                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 mt-1"
                                      />
                                    )}
                                  </div>

                                  <div className="flex gap-2 pt-2">
                                    <button
                                      onClick={() => handleGapAction(gap.gap_id, 'added')}
                                      disabled={saving}
                                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                                    >
                                      <CheckCircle className="w-3 h-3" />
                                      Confirm & Add
                                    </button>
                                    <button
                                      onClick={() => toggleGapConfiguration(gap.gap_id)}
                                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}

                              {!isConfiguring && (
                                <div className="flex gap-2 flex-wrap">
                                  <button
                                    onClick={() => toggleGapConfiguration(gap.gap_id)}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                                  >
                                    <Plus className="w-3 h-3" />
                                    Add to Map
                                  </button>
                                  <button
                                    onClick={() => handleEditStep({ process_step_id: gap.gap_id, process_step_name: gap.step_name } as ValidationResult)}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-white border border-orange-300 text-orange-700 rounded text-xs font-medium hover:bg-orange-50 disabled:opacity-50"
                                  >
                                    <Edit3 className="w-3 h-3" />
                                    Edit Draft
                                  </button>
                                  <button
                                    onClick={() => handleGapAction(gap.gap_id, 'rejected')}
                                    disabled={saving}
                                    className="flex items-center justify-center gap-1 px-4 py-2 bg-white border border-red-300 text-red-700 rounded text-xs font-medium hover:bg-red-50 disabled:opacity-50"
                                  >
                                    <X className="w-3 h-3" />
                                    Reject
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {editingStep && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Step</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Step Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  placeholder="Update step description..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee (optional)</label>
                <select
                  value={customAssignee !== null ? '__custom__' : editForm.assignee}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setCustomAssignee(''); // Show input field
                    } else {
                      setEditForm({ ...editForm, assignee: e.target.value });
                      setCustomAssignee(null); // Hide input field
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white"
                >
                  <option value="">Select stakeholder...</option>
                  {stakeholders.map((stakeholder, index) => (
                    <option key={index} value={stakeholder}>
                      {stakeholder}
                    </option>
                  ))}
                  <option value="__custom__">-- Enter custom value --</option>
                </select>
                {customAssignee !== null && (
                  <input
                    type="text"
                    value={customAssignee}
                    placeholder="Enter custom assignee..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm mt-2"
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomAssignee(value);
                      // ✅ FIX: Update editForm with the actual value
                      setEditForm({ ...editForm, assignee: value.trim() || '' });
                    }}
                    autoFocus
                  />
                )}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setEditingStep(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editForm.name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[102]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Step Details</h3>
              <button
                onClick={() => setViewingDetails(null)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-4">
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase mb-1">Step Name</div>
                <div className="text-sm text-gray-900 font-medium">{viewingDetails.process_step_name}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">Validation Status</div>
                  <span className={`inline-block px-2 py-1 text-xs rounded ${
                    viewingDetails.validation_status === 'CLEAR_MATCH' ? 'bg-green-100 text-green-800' :
                    viewingDetails.validation_status === 'INFERENCE' ? 'bg-yellow-100 text-yellow-800' :
                    viewingDetails.validation_status === 'AMBIGUITY' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {viewingDetails.validation_status.replace('_', ' ')}
                  </span>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">Confidence Score</div>
                  <span className={`text-sm font-semibold ${
                    (viewingDetails.confidence_score || 0) >= 90 ? 'text-green-600' :
                    (viewingDetails.confidence_score || 0) >= 70 ? 'text-yellow-600' : 'text-orange-600'
                  }`}>
                    {viewingDetails.confidence_score}%
                  </span>
                </div>
              </div>

              {viewingDetails.source_reference && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">Source Reference</div>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    {viewingDetails.source_reference.document_name && (
                      <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <div className="text-xs text-gray-500">Document</div>
                          <div className="text-sm text-gray-900">{viewingDetails.source_reference.document_name}</div>
                        </div>
                      </div>
                    )}
                    {viewingDetails.source_reference.page && (
                      <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <div className="text-xs text-gray-500">Location</div>
                          <div className="text-sm text-gray-900">{viewingDetails.source_reference.page}</div>
                        </div>
                      </div>
                    )}
                    {viewingDetails.source_reference.section && (
                      <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <div className="text-xs text-gray-500">Section</div>
                          <div className="text-sm text-gray-900">{viewingDetails.source_reference.section}</div>
                        </div>
                      </div>
                    )}
                    {viewingDetails.source_reference.quoted_text && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Quoted Text</div>
                        <div className="text-sm text-gray-700 italic bg-white p-2 rounded border border-gray-200">
                          "{viewingDetails.source_reference.quoted_text}"
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {viewingDetails.reasoning && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">Analysis / Reasoning</div>
                  <div className="bg-blue-50 rounded-lg p-3 text-sm text-gray-700">
                    {viewingDetails.reasoning}
                  </div>
                </div>
              )}

              {viewingDetails.suggested_action && viewingDetails.suggested_action !== 'None - validated successfully' && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">Suggested Action</div>
                  <div className="bg-purple-50 rounded-lg p-3 text-sm text-purple-700">
                    {viewingDetails.suggested_action}
                  </div>
                </div>
              )}

              {viewingDetails.systems_involved_verification && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">Systems Verification</div>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    {viewingDetails.systems_involved_verification.systems_found_in_source && viewingDetails.systems_involved_verification.systems_found_in_source.length > 0 && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Found in Source</div>
                        <div className="flex flex-wrap gap-1">
                          {viewingDetails.systems_involved_verification.systems_found_in_source.map((sys, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">{sys}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {viewingDetails.systems_involved_verification.systems_not_found_in_source && viewingDetails.systems_involved_verification.systems_not_found_in_source.length > 0 && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Not Found in Source</div>
                        <div className="flex flex-wrap gap-1">
                          {viewingDetails.systems_involved_verification.systems_not_found_in_source.map((sys, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">{sys}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {viewingDetails.missing_information && viewingDetails.missing_information.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">Missing Information</div>
                  <div className="bg-yellow-50 rounded-lg p-3">
                    <ul className="text-sm text-yellow-800 space-y-1">
                      {viewingDetails.missing_information.map((info, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <AlertTriangle className="w-3 h-3 mt-1 flex-shrink-0" />
                          {info}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {viewingDetails.user_action && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">User Action</div>
                  <span className="inline-block px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                    {viewingDetails.user_action}
                  </span>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => setViewingDetails(null)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {notesStep && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[102]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add Notes</h3>
              <button
                onClick={() => setNotesStep(null)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                <span className="font-medium">Step:</span> {notesStep.process_step_name}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign To
                </label>
                <input
                  type="text"
                  value={notesForm.assigned_to}
                  onChange={(e) => setNotesForm({ ...notesForm, assigned_to: e.target.value })}
                  placeholder="e.g., John Smith, Risk Team"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes / Comments
                </label>
                <textarea
                  value={notesForm.notes}
                  onChange={(e) => setNotesForm({ ...notesForm, notes: e.target.value })}
                  placeholder="Add any comments, observations, or instructions..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-2">
              <button
                onClick={() => setNotesStep(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                disabled={saving}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Notes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Suggestion Modal */}
      {showSuggestionModal && currentSuggestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[103]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">AI Suggested Improvement</h3>
              </div>
              <button
                onClick={() => {
                  setShowSuggestionModal(false);
                  setCurrentSuggestion(null);
                  setSuggestionStep(null);
                }}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Comparison Section */}
              <div className="grid grid-cols-2 gap-4">
                {/* Current Step */}
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    Current Step
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs font-medium text-gray-600">Step Name:</span>
                      <p className="text-sm text-gray-900 mt-1">{currentSuggestion.original_step.step_name}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-600">Status:</span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-1 text-xs rounded ${
                          currentSuggestion.original_step.validation_status === 'INFERENCE' ? 'bg-blue-100 text-blue-800' :
                          currentSuggestion.original_step.validation_status === 'AMBIGUITY' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          {currentSuggestion.original_step.validation_status}
                        </span>
                        <span className="text-xs text-gray-600">
                          {currentSuggestion.original_step.confidence_score}% confidence
                        </span>
                      </div>
                    </div>
                    {currentSuggestion.original_step.ambiguities?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-gray-600">Ambiguities:</span>
                        <ul className="text-xs text-gray-700 mt-1 list-disc list-inside space-y-1">
                          {currentSuggestion.original_step.ambiguities.map((amb: string, idx: number) => (
                            <li key={idx}>{amb}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {currentSuggestion.original_step.assumptions?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-gray-600">Assumptions:</span>
                        <ul className="text-xs text-gray-700 mt-1 list-disc list-inside space-y-1">
                          {currentSuggestion.original_step.assumptions.map((assum: string, idx: number) => (
                            <li key={idx}>{assum}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {currentSuggestion.original_step.missing_info?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-gray-600">Missing Info:</span>
                        <ul className="text-xs text-gray-700 mt-1 list-disc list-inside space-y-1">
                          {currentSuggestion.original_step.missing_info.map((info: string, idx: number) => (
                            <li key={idx}>{info}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Suggested Step */}
                <div className="border-2 border-purple-300 rounded-lg p-4 bg-purple-50">
                  <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    AI Suggested Improvement
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs font-medium text-purple-700">Step Name:</span>
                      <p className="text-sm text-gray-900 mt-1 font-medium">{currentSuggestion.suggestion.step_name}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-purple-700">Description:</span>
                      <p className="text-sm text-gray-900 mt-1">{currentSuggestion.suggestion.description}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-purple-700">Expected Confidence:</span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800 font-medium">
                          {currentSuggestion.suggestion.confidence_improvement}% confidence
                        </span>
                        <span className="text-xs text-green-600 font-medium">
                          +{currentSuggestion.suggestion.confidence_improvement - currentSuggestion.original_step.confidence_score}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reasoning Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Why This Improves the Step</h4>
                <p className="text-sm text-gray-700">{currentSuggestion.suggestion.reasoning}</p>
              </div>

              {/* Changes Made Section */}
              {currentSuggestion.suggestion.changes?.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 mb-2">Changes Being Made</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    {currentSuggestion.suggestion.changes.map((change: string, idx: number) => (
                      <li key={idx}>{change}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex gap-3 bg-gray-50">
              <button
                onClick={() => {
                  setShowSuggestionModal(false);
                  setCurrentSuggestion(null);
                  setSuggestionStep(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAcceptSuggestion}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Accept & Apply Suggestion
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteStepConfirmation.isOpen && deleteStepConfirmation.step && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-100">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Delete Step</h2>
              </div>
              <button
                onClick={() => setDeleteStepConfirmation({ isOpen: false, step: null })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed">
                Are you sure you want to permanently delete this step from the process map?
              </p>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900 text-sm">
                  {deleteStepConfirmation.step.process_step_name}
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                This action will remove the step from the process map and automatically renumber all remaining steps.
                Unlike "Reject", this action permanently removes the step and cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setDeleteStepConfirmation({ isOpen: false, step: null })}
                disabled={saving}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteStep(deleteStepConfirmation.step!.process_step_id)}
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ValidationCommandCenter;
